import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { REFERRAL_COMPONENTS } from '../../../../constants/referral.constants';
import { ErrorMessages } from '../../../../config/error-messages';

import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { Config } from '../../../../config/config';
import { ServiceRouter } from '../../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../../constants/service.constants';
import { Environment } from '../../../../config/environment';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

/**
 * My referral requests component
 */
@Component({
	selector: 'app-my-referral-requests',
	templateUrl: './my-referral-requests.component.html',
	styleUrls: ['./my-referral-requests.component.scss'],
})
export class MyReferralRequestsComponent implements OnDestroy {
	/** My referral request data */
	data: any;
	/** Show all for expand/collapse */
	showAll: any = [];
	/** Is current referral flag */
	isCurrentReferral: boolean = false;
	/** Referral component constants */
	referralComponents: any = REFERRAL_COMPONENTS;
	/** Data carrier subscription */
	subscriptions: Subscription[] = [];
	/** No data message string */
	noDataMsg: string;
	/** No data flag */
	noData: boolean = false;

	/**
	 * My referral requests constructor
	 * @param activatedRoute contains the information about route associated
	 * @param errorMsg error messages
	 * @param spinner spinner component
	 * @param config config file that holds the service end points
	 * @param serviceRouter service router
	 * @param environment environment file that supplies env values
	 */
	constructor(
		private activatedRoute: ActivatedRoute,
		private errorMsg: ErrorMessages,
		private spinner: SpinnerComponent,
		private config: Config,
		private serviceRouter: ServiceRouter,
		private environment: Environment
	) {
		this.getInsuranceNames();
		this.getInsuranceTypes();
		this.subscriptions.push(
			this.activatedRoute.params.subscribe(params => {
				try {
					this.isCurrentReferral = params.section === 'current';
				} catch (Exception) {}
				if (this.isCurrentReferral) {
					this.getCurrentReferralRequests();
				} else {
					this.getPastReferralRequests();
				}
				// Initializing showAll[] array for collapse and expand
				this.initializeShowAllArray();
			})
		);
	}

	insuranceNameLookup = [];
	insuranceTypeLookup = [];

	/**
	 * Retrieve insurance plans list
	 */
	getInsuranceNames() {
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getInsurancePlans,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.insuranceNameLookup = response.insurance_plan;
					},
					err => {
						this.spinner.stopLoader();
					}
				)
		);
	}

	/**
	 * Retrieve insurance types list
	 */
	getInsuranceTypes() {
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getInsuranceTypes,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.insuranceTypeLookup = response.insurance_type;
					},
					err => {
						this.spinner.stopLoader();
					}
				)
		);
	}

	/**
	 * Initializing showAll[] array for collapse and expand
	 */
	initializeShowAllArray() {
		if (this.data && this.data.category) {
			this.data.category.forEach(category => {
				if (category.referral && category.name === this.referralComponents.REFERRAL_READY) {
					category.referral.forEach(referral => {
						this.showAll.push(false);
					});
				}
			});
		}
	}

	/**
	 * Retrieve current referral requests list
	 */
	getCurrentReferralRequests() {
		this.data = '';
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getCurrentReferrals + '?category=my_requests',
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.data = response;
						if (!this.data || !this.data.category || this.data.category.length === 0) {
							this.noData = true;
							this.noDataMsg = this.errorMsg.noData.referralRequests;
						}
					},
					err => {
						this.spinner.stopLoader();
						this.noData = true;
						this.noDataMsg = this.errorMsg.noData.referralRequests;
					}
				)
		);
	}

	/**
	 * Retrieve past referral requests list
	 */
	getPastReferralRequests() {
		this.data = '';
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getPastReferrals + '?category=my_requests',
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.data = response;
						if (!this.data || !this.data.category || this.data.category.length === 0) {
							this.noData = true;
							this.noDataMsg = this.errorMsg.noData.referralRequests;
						}
					},
					err => {
						this.spinner.stopLoader();
						this.noData = true;
						this.noDataMsg = this.errorMsg.noData.referralRequests;
					}
				)
		);
	}

	/**
	 * Print referral requests details
	 */
	print() {
		window.print();
	}

	/**
	 * Returns date in 'MMM DD, YYYY' format
	 */
	getFormattedDate(date) {
		return moment(new Date(date)).format('MMM DD, YYYY');
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}

	getInsurancePlanName(id: string) {
		const insuranceName = this.insuranceNameLookup.filter(insurance => insurance.id === id);
		return insuranceName.length > 0 ? insuranceName[0].name : '';
	}

	getInsurancePlanType(typeId: string) {
		const insuranceType = this.insuranceTypeLookup.filter(insurance => insurance.id === typeId);
		return insuranceType.length > 0 ? insuranceType[0].name : '';
	}
}
